import React, { useState, useEffect } from "react";
import Button from "src/components/app/Button";
import Input from "src/components/app/Input";
import Select from "src/components/app/Select";
import { saveReminder, updateReminder } from "src/helpers/saveReminder";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import reminders from "src/constants/reminders";
import { toast } from "sonner";
import {
  ReminderData,
  formatReminderForForm,
} from "src/helpers/reminderHelpers";
import moment from "moment";
import * as reminderActions from "src/redux/slices/reminder";

export type ReminderType =
  | "WATER"
  | "SLEEP"
  | "STEPS"
  | "FATIGUE"
  | "DIET"
  | "MEDICATION"
  | "EXERCISE";

interface ReminderFormContentProps {
  reminderType: ReminderType;
  existingReminder: ReminderData | null;
  onClose: () => void;
  /** When true, form is visible and will init/reset (e.g. sheet is open) */
  visible: boolean;
}

const ReminderFormContent: React.FC<ReminderFormContentProps> = ({
  reminderType,
  existingReminder,
  onClose,
  visible,
}) => {
  const dispatch = useAppDispatch();
  const { patient } = useAppSelector((state) => state.auth);
  const [formData, setFormData] = useState({
    reminder_time: "",
    frequency: reminders.FREQUENCY_OPTIONS[0].value,
    start_date: "",
    end_date: "",
    selectDays: [] as string[],
  });

  useEffect(() => {
    if (!visible) return;
    if (existingReminder) {
      const formattedData = formatReminderForForm(existingReminder);
      setFormData({
        reminder_time: formattedData.customFreqAttributes.defaultTimes[0] || "",
        frequency: formattedData.frequency,
        start_date: moment(formattedData.startTime).format("YYYY-MM-DD"),
        end_date: moment(formattedData.endTime).format("YYYY-MM-DD"),
        selectDays: formattedData.customFreqAttributes.days || [],
      });
    } else {
      setFormData({
        reminder_time: "",
        frequency: reminders.FREQUENCY_OPTIONS[0].value,
        start_date: "",
        end_date: "",
        selectDays: [],
      });
    }
  }, [existingReminder, visible]);

  const refetchReminders = () => {
    if (patient?.data?.patient_id) {
      new Promise((resolve, reject) => {
        dispatch(
          reminderActions.getReminderRequest({
            resolve,
            reject,
            payload: { user_id: patient?.data?.patient_id },
          })
        );
      }).catch((error) => {
        console.error("Error refetching reminders:", error);
      });
    }
  };

  const handleSaveReminder = () => {
    if (new Date(formData.start_date) > new Date(formData.end_date)) {
      toast.error("Start Date must be before End Date");
      return;
    }
    const payload: any = {
      startTime: formData.start_date,
      endTime: formData.end_date,
      frequency: formData.frequency,
      customFreqAttributes: {
        days: formData.selectDays,
        defaultTimes: [formData.reminder_time],
      },
    };
    if (existingReminder) {
      payload.patientReminderId = existingReminder.patientReminderId;
      updateReminder(payload, dispatch, () => {
        refetchReminders();
        onClose();
      });
    } else {
      payload.type = reminderType;
      saveReminder(payload, dispatch, () => {
        refetchReminders();
        onClose();
      });
    }
  };

  return (
    <div className="relative space-y-4">
      <div className="grid grid-cols-1 gap-y-3">
        <div className="col-span-1">
          <div className="relative w-full">
            <Input
              label="Reminder Time"
              name="reminder_time"
              type="time"
              placeholder="Enter Reminder Time"
              fullWidth
              value={formData.reminder_time}
              step="60"
              onChange={(e) => {
                setFormData({ ...formData, reminder_time: e.target.value });
              }}
            />
          </div>
        </div>
        <div className="col-span-1">
          <div className="relative w-full">
            <Select
              label="Frequency"
              name="frequency"
              options={reminders.FREQUENCY_OPTIONS}
              fullWidth
              value={formData.frequency}
              onChange={(e) => {
                setFormData({ ...formData, frequency: e.target.value });
              }}
            />
          </div>
        </div>
        {formData.frequency === "custom" && (
          <div className="w-full">
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
              Custom Days
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              {reminders.CUSTOM_FREQUENCY_OPTIONS.map((item: { day: string }) => {
                const dayVal = item.day;
                const isSelected = formData.selectDays.includes(dayVal);
                return (
                  <button
                    key={dayVal}
                    type="button"
                    onClick={() =>
                      setFormData({
                        ...formData,
                        selectDays: isSelected
                          ? formData.selectDays.filter((d) => d !== dayVal)
                          : [...formData.selectDays, dayVal],
                      })
                    }
                    className={`w-8 h-8 rounded-full text-sm ${
                      isSelected
                        ? "bg-[var(--app-primary)] text-white"
                        : "bg-gray-200 text-[var(--text-secondary)]"
                    }`}
                  >
                    {dayVal.charAt(0).toUpperCase()}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <div className="col-span-1">
          <div className="relative w-full">
            <Input
              label="Start Date"
              name="start_date"
              type="date"
              placeholder="Enter Start Date"
              fullWidth
              value={formData.start_date}
              onChange={(e) => {
                setFormData({ ...formData, start_date: e.target.value });
              }}
            />
          </div>
        </div>
        <div className="col-span-1">
          <div className="relative w-full">
            <Input
              label="End Date (Optional)"
              name="end_date"
              type="date"
              placeholder="Enter End Date"
              fullWidth
              value={formData.end_date}
              onChange={(e) => {
                setFormData({ ...formData, end_date: e.target.value });
              }}
            />
          </div>
        </div>
        <div className="col-span-1 flex gap-2">
          <Button
            variant="outlined"
            rounded="full"
            fullWidth
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            rounded="full"
            fullWidth
            onClick={handleSaveReminder}
          >
            Save Reminder
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReminderFormContent;
